import React from 'react';
import { useLanguage, Language } from '../contexts/LanguageContext';
import { LegalPage, LegalDoc } from '../components/LegalContent';

// EN/ES finalized (drafted 2026-09-21, approved by Manuel — cancellation
// policy: 100% refund before intake starts, none after; photo add-on: free
// retake only, never a money refund). HT/VI = best effort machine
// translation, pending native review — same convention used for the rest of
// the site's HT/VI content.
const DOCS: Record<Language, LegalDoc> = {
  es: {
    title: 'Política de Reembolsos',
    updated: 'Última actualización: 21 de septiembre de 2026',
    sections: [{
      heading: 'Servicio principal (preparación de formulario)',
      blocks: [{
        type: 'table',
        headers: ['Momento de la cancelación', 'Reembolso'],
        rows: [['Antes de completar el cuestionario (aún no empezamos a trabajar su caso)', '100% del precio del servicio'], ['Después de que la preparación del formulario ya comenzó (revisión humana en curso)', 'Sin reembolso — el trabajo ya fue entregado']]
      }, {
        type: 'p',
        text: 'Si tiene un error nuestro en el formulario ya entregado, no aplica reembolso: aplica la **garantía de corrección gratis** (vea los Términos de Servicio) — lo corregimos sin costo dentro de un plazo razonable.'
      }, {
        type: 'p',
        text: '**La tarifa que cobra el gobierno de EE. UU.** nunca se reembolsa a través de Noor, porque Noor no la cobra: usted la paga directo al gobierno.'
      }]
    }, {
      heading: 'Foto de pasaporte (add-on)',
      blocks: [{
        type: 'p',
        text: 'Este servicio se entrega **validado, nunca garantizado**. No ofrecemos reembolso de dinero por este add-on. Si la agencia del gobierno rechaza la foto o esta no pasa la validación, usted tiene derecho a una **re-toma sin costo adicional**, dentro de un plazo razonable, hasta lograr una foto que cumpla las reglas oficiales.'
      }]
    }, {
      heading: 'Traducción certificada (si la contrata)',
      blocks: [{
        type: 'p',
        text: 'Se rige por los términos del proveedor de traducción con el que Noor trabaja; Noor le informará esos términos antes de confirmar ese servicio.'
      }]
    }, {
      heading: 'Cómo pedir un reembolso',
      blocks: [{
        type: 'p',
        text: 'Escriba a info@noorpassportservices.com indicando su nombre y el servicio contratado. Respondemos en el idioma de su preferencia y, si corresponde, procesamos el reembolso por el mismo método de pago original.'
      }]
    }],
    contact: 'Noor Passport Services (DBA of Noor Metabolico LLC) — info@noorpassportservices.com'
  },
  en: {
    title: 'Refund Policy',
    updated: 'Last updated: September 21, 2026',
    sections: [{
      heading: 'Main service (form preparation)',
      blocks: [{
        type: 'table',
        headers: ['When you cancel', 'Refund'],
        rows: [['Before completing the questionnaire (we have not started working on your case yet)', '100% of the service price'], ['After form preparation has already started (human review in progress)', 'No refund — the work has already been delivered']]
      }, {
        type: 'p',
        text: 'If there is an error on our part in a form already delivered, no refund applies: the **free-correction guarantee** applies instead (see Terms of Service) — we correct it at no cost within a reasonable time.'
      }, {
        type: 'p',
        text: '**The fee the U.S. government charges** is never refunded through Noor, because Noor does not charge it: you pay it directly to the government.'
      }]
    }, {
      heading: 'Passport photo (add-on)',
      blocks: [{
        type: 'p',
        text: 'This service is delivered **validated, never guaranteed**. We do not offer a money refund for this add-on. If the government agency rejects the photo or it fails validation, you are entitled to a **free retake**, within a reasonable time, until you have a photo that meets the official rules.'
      }]
    }, {
      heading: 'Certified translation (if purchased)',
      blocks: [{
        type: 'p',
        text: 'This is governed by the terms of the translation provider Noor works with; Noor will inform you of those terms before confirming that service.'
      }]
    }, {
      heading: 'How to request a refund',
      blocks: [{
        type: 'p',
        text: 'Write to info@noorpassportservices.com with your name and the service you purchased. We respond in your preferred language and, where applicable, process the refund to the original payment method.'
      }]
    }],
    contact: 'Noor Passport Services (DBA of Noor Metabolico LLC) — info@noorpassportservices.com'
  },
  ht: {
    title: 'Politik Ranbousman',
    updated: 'Dènye mizajou: 21 septanm 2026',
    sections: [{
      heading: 'Sèvis prensipal la (preparasyon fòm nan)',
      blocks: [{
        type: 'table',
        headers: ['Kilè ou anile', 'Ranbousman'],
        rows: [['Anvan ou fin ranpli kesyonè a (nou poko kòmanse travay sou ka ou)', '100% pri sèvis la'], ['Apre preparasyon fòm nan deja kòmanse (revizyon imen an ankou)', 'Pa gen ranbousman — travay la deja livre']]
      }, {
        type: 'p',
        text: 'Si gen yon erè ki soti nan bò nou nan yon fòm ki deja livre, pa gen ranbousman: se **garanti korije gratis la** ki aplike (gade Kondisyon Sèvis) — nou korije l gratis nan yon delè rezonab.'
      }, {
        type: 'p',
        text: '**Frè gouvènman ameriken an chaje a** pa janm ranbouse atravè Noor, paske Noor pa chaje l: ou peye l dirèkteman bay gouvènman an.'
      }]
    }, {
      heading: 'Foto paspò (add-on)',
      blocks: [{
        type: 'p',
        text: 'Sèvis sa a livre **valide, pa janm garanti**. Nou pa ofri ranbousman lajan pou add-on sa a. Si ajans gouvènman an rejte foto a oswa li pa pase validasyon an, ou gen dwa a yon **re-pran san kou adisyonèl**, nan yon delè rezonab, jiskaske ou gen yon foto ki respekte règ ofisyèl yo.'
      }]
    }, {
      heading: 'Tradiksyon sètifye (si ou achte l)',
      blocks: [{
        type: 'p',
        text: 'Sa gouvène pa kondisyon founisè tradiksyon Noor travay avè l; Noor ap enfòme ou de kondisyon sa yo anvan konfime sèvis sa a.'
      }]
    }, {
      heading: 'Kijan pou mande yon ranbousman',
      blocks: [{
        type: 'p',
        text: 'Ekri nou nan info@noorpassportservices.com epi endike non ou ak sèvis ou te achte a. Nou repon n nan lang ou pi pito epi, si sa aplikab, nou trete ranbousman an nan menm metòd peman orijinal la.'
      }]
    }],
    contact: 'Noor Passport Services (DBA of Noor Metabolico LLC) — info@noorpassportservices.com'
  },
  vi: {
    title: 'Chính Sách Hoàn Tiền',
    updated: 'Cập nhật lần cuối: ngày 21 tháng 9 năm 2026',
    sections: [{
      heading: 'Dịch vụ chính (chuẩn bị đơn)',
      blocks: [{
        type: 'table',
        headers: ['Thời điểm hủy', 'Hoàn tiền'],
        rows: [['Trước khi hoàn thành bảng câu hỏi (chúng tôi chưa bắt đầu xử lý trường hợp của quý vị)', 'Hoàn 100% giá dịch vụ'], ['Sau khi việc chuẩn bị đơn đã bắt đầu (đang xem xét bởi con người)', 'Không hoàn tiền — công việc đã được thực hiện']]
      }, {
        type: 'p',
        text: 'Nếu có lỗi do chúng tôi gây ra trong đơn đã giao, sẽ không áp dụng hoàn tiền: thay vào đó áp dụng **bảo đảm sửa lỗi miễn phí** (xem Điều Khoản Dịch Vụ) — chúng tôi sửa miễn phí trong thời gian hợp lý.'
      }, {
        type: 'p',
        text: '**Lệ phí chính phủ Hoa Kỳ thu** không bao giờ được hoàn qua Noor, vì Noor không thu khoản này: quý vị thanh toán trực tiếp cho chính phủ.'
      }]
    }, {
      heading: 'Ảnh hộ chiếu (dịch vụ bổ sung)',
      blocks: [{
        type: 'p',
        text: 'Dịch vụ này được giao ở trạng thái **đã xác thực, không bao giờ được đảm bảo**. Chúng tôi không hoàn tiền cho dịch vụ bổ sung này. Nếu cơ quan chính phủ từ chối ảnh hoặc ảnh không đạt xác thực, quý vị có quyền **chụp lại miễn phí**, trong thời gian hợp lý, cho đến khi có được ảnh đáp ứng quy định chính thức.'
      }]
    }, {
      heading: 'Dịch thuật có chứng nhận (nếu mua)',
      blocks: [{
        type: 'p',
        text: 'Dịch vụ này tuân theo điều khoản của nhà cung cấp dịch thuật mà Noor hợp tác; Noor sẽ thông báo cho quý vị các điều khoản đó trước khi xác nhận dịch vụ.'
      }]
    }, {
      heading: 'Cách yêu cầu hoàn tiền',
      blocks: [{
        type: 'p',
        text: 'Viết thư đến info@noorpassportservices.com kèm theo họ tên và dịch vụ quý vị đã mua. Chúng tôi phản hồi bằng ngôn ngữ quý vị chọn và, nếu áp dụng, hoàn tiền theo phương thức thanh toán ban đầu.'
      }]
    }],
    contact: 'Noor Passport Services (DBA of Noor Metabolico LLC) — info@noorpassportservices.com'
  }
};
export function RefundPolicyPage() {
  const {
    language
  } = useLanguage();
  return <LegalPage doc={DOCS[language]} data-id="element-656" data-mp="219mdpfwtrwfc" />;
}